import type { ChangeEvent } from 'react';
import { useState } from 'react';

import styles from './RichSelector.module.css';

import { Picture } from '../Picture';
import type { TextProps } from '../Text';
import { Text } from '../Text';
import type { RadioButtonChangeEventHandler } from './RadioButton/RadioButton';
import { RadioButton } from './RadioButton/RadioButton';

export type RichSelectorValue = string | number | undefined | null;

export type RichSelectorChangeEventHandler = (
  value: RichSelectorValue,
  e: ChangeEvent<HTMLInputElement>
) => void;

export interface RichSelectorOption {
  value: RichSelectorValue;
  label: string;
  description: string;
  /** The source of the images representing the option. */
  image: {
    /** The image shown on mobile screens when no option is selected. */
    small: string[];
    /** The image shown on larger screens or when an option is selected. */
    large: string[];
  };
  /** The alternate text for the image. */
  imageAlt: string;
}

export interface RichSelectorProps {
  name: string;
  value?: RichSelectorValue;
  options: RichSelectorOption[];
  onChange?: RichSelectorChangeEventHandler;
  /**
   * Display the option description at the bottom of the control, rather than
   * below each option.
   */
  descriptionAtBottom?: boolean;
  /**
   * The default full width description to fall back on when no option is
   * selected.
   */
  defaultDescription?: string;
  /** Description text color */
  descriptionAtBottomColor?: TextProps['color'];
  /** Default image to be displayed when no selection has been made. */
  defaultImageIndex?: number;
}

/**
 * Render a group of radio inputs with additional rich content including an
 * image and extended description.
 *
 * @example
 *
 * ```
 * <RichSelector
 *   name="eating-style"
 *   options={[
 *     {
 *       label: 'Very picky',
 *       image: ['/img/example.jpg'],
 *       description: 'My dog is a picky all the time',
 *       alt: 'Image of dog looking at food,
 *       value: 'very-picky',
 *     },
 *   {...additionalOptions}
 *   ]}
 * />
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/richselector
 */
export function RichSelector(props: RichSelectorProps) {
  const [selectedOption, setSelectedOption] = useState(
    props.options.find(option => option.value === props.value)
  );

  // Options that are either selected or hovered.
  const [activeOption, setActiveOption] = useState(selectedOption);

  const handleChange: RadioButtonChangeEventHandler = (option, e) => {
    setSelectedOption(option);
    props.onChange?.(option.value, e);
  };

  return (
    <div className={styles.root}>
      <div className={styles.activeImage}>
        {/* Show image for selected option (mobile screens only). */}
        {selectedOption && activeOption && (
          <Picture
            sources={activeOption.image.large}
            alt={activeOption.imageAlt}
          />
        )}
        {selectedOption === undefined && props.defaultImageIndex && (
          <Picture
            sources={props.options[props.defaultImageIndex].image.small}
            alt={props.options[props.defaultImageIndex].imageAlt}
          />
        )}
      </div>
      <div className={styles.options}>
        {props.options.map((option, i) => (
          <RadioButton
            key={i}
            option={option}
            name={props.name}
            showDescription={!props.descriptionAtBottom}
            checked={selectedOption?.value === option.value}
            isFirst={i === 0}
            isLast={i === props.options.length - 1}
            onChange={handleChange}
            onMouseEnter={setActiveOption}
            onMouseLeave={() => setActiveOption(selectedOption)}
            onBlur={() => !selectedOption && setActiveOption(undefined)}
            onFocus={setActiveOption}
            showMobileImg={
              selectedOption === undefined && !props.defaultImageIndex
            }
          />
        ))}
      </div>
      {props.descriptionAtBottom && (
        <Text
          aria-hidden
          as="div"
          variant="heading-16"
          className={styles.description}
          color={props.descriptionAtBottomColor ?? 'Carrot2'}
          topSpacing="lg"
          bottomSpacing="md"
          fontStyle="italic"
        >
          {activeOption?.description ?? props.defaultDescription}
        </Text>
      )}
    </div>
  );
}
